import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Form } from "react-bootstrap";
import { FaDownload, FaFileUpload, FaPlay } from "react-icons/fa";
import { FiClock, FiUploadCloud } from "react-icons/fi";
import toast from "react-hot-toast";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import SideBar from "../../components/sidebar/Sidebar";
import ResponsiveRows from "../../components/ResponsiveRows";
import UserLoginData from "../Auth/UserLoginData";
import { useListHook } from "../Admin/List/hook";
import { useUpdateStatus } from "../Admin/Modal/ClientsAssign/hook";
import {
  getBulkDownloadUrl,
  getBulkUploadRowsApi,
  listBulkUploadsApi,
  startBulkUploadProcessingApi,
  uploadBulkFileApi,
} from "./api";

export const BulkUploadScreen = () => {
  const { isLoggedIn } = UserLoginData();
  const { clientList } = useListHook();
  const { defaultData } = useUpdateStatus(isLoggedIn && isLoggedIn.response._id);
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedUploadId = searchParams.get("uploadId");
  const routeUpload = location.state?.upload;
  const initialUpload = routeUpload?._id === selectedUploadId ? routeUpload : null;
  const previewRequestId = useRef(0);
  const processingStartId = useRef("");
  const currentUploadRef = useRef(initialUpload);

  const [selectedClientId, setSelectedClientId] = useState("");
  const [selectedClientName, setSelectedClientName] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewRows, setPreviewRows] = useState([]);
  const [previewCount, setPreviewCount] = useState(0);
  const [previewPage, setPreviewPage] = useState(0);
  const [currentUpload, setCurrentUpload] = useState(initialUpload);
  const [previewError, setPreviewError] = useState("");
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [startingUploadId, setStartingUploadId] = useState("");

  const visibleClients =
    isLoggedIn && isLoggedIn.response.role !== "admin" ? defaultData : clientList;

  useEffect(() => {
    const role = isLoggedIn?.response?.role;
    if (role === "client") {
      setSelectedClientId(isLoggedIn.response._id);
      setSelectedClientName(isLoggedIn.response.username);
      return;
    }
    const clientId = searchParams.get("clientId");
    if (clientId) {
      setSelectedClientId(clientId);
      setSelectedClientName(visibleClients.find((client) => client._id === clientId)?.username || "");
    }
  }, [isLoggedIn, searchParams, visibleClients]);

  const previewTotalPages = useMemo(
    () => Math.max(1, Math.ceil(previewCount / 50)),
    [previewCount]
  );

  const downloadTemplate = () => {
    const csv = "title,description,long_link\nSample title,Sample description,https://example.com/page\n";
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = "bulk-upload-template.csv";
    anchor.click();
    window.URL.revokeObjectURL(url);
  };

  const updateCurrentUpload = useCallback((value) => {
    const upload = typeof value === "function" ? value(currentUploadRef.current) : value;
    currentUploadRef.current = upload;
    setCurrentUpload(upload);
  }, []);

  const loadPreviewRows = useCallback(async (uploadId, page = 0) => {
    const requestId = ++previewRequestId.current;
    setPreviewError("");
    setLoadingPreview(true);
    try {
      const [data, error] = await getBulkUploadRowsApi(uploadId, page, 50);
      if (requestId !== previewRequestId.current) return;
      if (error || !Array.isArray(data?.rows)) {
        throw error || new Error("Could not load this upload.");
      }

      let upload = data.upload || null;
      if (!upload) {
        const role = isLoggedIn?.response?.role || "admin";
        const requestClientId = role === "client"
          ? isLoggedIn?.response?._id
          : role === "operational"
            ? (isLoggedIn?.response?.clients || [])
            : selectedClientId || searchParams.get("clientId") || "all";
        const limit = 50;
        for (let pageIndex = 0; ; pageIndex += 1) {
          const [listData, listError] = await listBulkUploadsApi(
            requestClientId,
            role,
            pageIndex,
            limit
          );
          if (requestId !== previewRequestId.current) return;
          if (listError) throw listError;
          upload = listData?.uploads?.find((item) => item._id === uploadId);
          if (upload || !listData?.uploads?.length || (pageIndex + 1) * limit >= (listData.count || 0)) {
            break;
          }
        }
      }
      if (!upload && currentUploadRef.current?._id === uploadId) {
        upload = currentUploadRef.current;
      }
      if (!upload) throw new Error("Upload not found.");

      if (processingStartId.current === uploadId && upload.status === "UPLOADED") {
        updateCurrentUpload({ ...upload, status: "PROCESSING" });
      } else {
        if (processingStartId.current === uploadId) processingStartId.current = "";
        updateCurrentUpload(upload);
      }
      setPreviewRows(data.rows || []);
      setPreviewCount(data.count || 0);
      setPreviewPage(page);
    } catch {
      if (requestId !== previewRequestId.current) return;
      setPreviewError("Could not load this upload.");
      updateCurrentUpload(null);
      setPreviewRows([]);
      setPreviewCount(0);
    } finally {
      if (requestId === previewRequestId.current) setLoadingPreview(false);
    }
  }, [isLoggedIn, searchParams, selectedClientId, updateCurrentUpload]);

  useEffect(() => {
    if (!selectedUploadId) {
      previewRequestId.current += 1;
      processingStartId.current = "";
      updateCurrentUpload(null);
      setPreviewRows([]);
      setPreviewCount(0);
      setPreviewPage(0);
      setPreviewError("");
      setLoadingPreview(false);
      return undefined;
    }
    if (processingStartId.current && processingStartId.current !== selectedUploadId) {
      processingStartId.current = "";
    }
    loadPreviewRows(selectedUploadId, 0);
    return () => {
      previewRequestId.current += 1;
    };
  }, [isLoggedIn?.response?._id, loadPreviewRows, selectedUploadId, updateCurrentUpload]);

  useEffect(() => {
    if (currentUpload?.status !== "PROCESSING") return undefined;
    const interval = setInterval(() => loadPreviewRows(currentUpload._id, previewPage), 5000);
    return () => clearInterval(interval);
  }, [currentUpload?._id, currentUpload?.status, loadPreviewRows, previewPage]);

  const handleClientChange = (event) => {
    const nextClientId = event.target.value;
    const nextClient = visibleClients.find((client) => client._id === nextClientId);
    setSelectedClientId(nextClientId);
    setSelectedClientName(nextClient?.username || "");
  };

  const handleFileChange = (event) => {
    setSelectedFile(event.target.files?.[0] || null);
  };

  const handleUpload = async () => {
    if (!selectedClientId || !selectedClientName) {
      toast.error("Please select a client first.");
      return;
    }

    if (!selectedFile) {
      toast.error("Please choose a CSV or XLSX file.");
      return;
    }

    const fileName = selectedFile.name.toLowerCase();
    if (!fileName.endsWith(".csv") && !fileName.endsWith(".xlsx")) {
      toast.error("Only CSV and XLSX files are supported.");
      return;
    }

    setUploading(true);
    const [data, error] = await uploadBulkFileApi({
      file: selectedFile,
      clientId: selectedClientId,
      clientName: selectedClientName,
      loginUserId: isLoggedIn?.response?._id,
    });

    if (error) {
      toast.error(error.response?.data?.message || "Upload failed.");
      setUploading(false);
      return;
    }

    toast.success("File uploaded. Preview is ready.");
    updateCurrentUpload(data.upload);
    setPreviewRows(data.preview || []);
    setPreviewCount(data.upload?.totalRows || 0);
    setPreviewPage(0);
    setPreviewError("");
    if (data.upload?._id) {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.set("uploadId", data.upload._id);
      nextParams.set("clientId", selectedClientId);
      setSearchParams(nextParams, { replace: true });
    }
    setSelectedFile(null);
    const input = document.getElementById("bulk-upload-input");
    if (input) {
      input.value = "";
    }
    setUploading(false);
  };

  const handleStartProcessing = async (uploadId) => {
    setStartingUploadId(uploadId);
    const [data, error] = await startBulkUploadProcessingApi(
      uploadId,
      isLoggedIn?.response?._id
    );

    if (error) {
      toast.error(error.response?.data?.message || "Could not start processing.");
      setStartingUploadId("");
      return;
    }

    toast.success(data?.message || "Bulk processing started.");
    if (currentUpload?._id === uploadId) {
      processingStartId.current = uploadId;
      updateCurrentUpload((upload) => upload ? { ...upload, status: "PROCESSING" } : upload);
    }
    setStartingUploadId("");
  };

  const openHistory = () => {
    const params = new URLSearchParams();
    const clientId = selectedClientId || searchParams.get("clientId");
    if (clientId) params.set("clientId", clientId);
    const query = params.toString();
    navigate(`/bulk-uploads${query ? `?${query}` : ""}`);
  };

  return (
    <SideBar>
      <section className="bulk-page" aria-labelledby="bulk-page-title">
        <header className="bulk-page-header">
          <div className="bulk-page-intro">
            <span className="create-page-icon" aria-hidden="true"><FiUploadCloud /></span>
            <div>
              <h1 id="bulk-page-title">Bulk upload</h1>
              <p>Import links from a CSV or XLSX file.</p>
            </div>
          </div>
          <div className="bulk-page-header-actions">
            <button type="button" className="bulk-secondary-button" onClick={openHistory}>
              <FiClock aria-hidden="true" />
              Upload history
            </button>
            <button type="button" className="bulk-template-button" onClick={downloadTemplate}>
              <FaDownload aria-hidden="true" />
              Download template
            </button>
          </div>
        </header>

        <section className="bulk-upload-form" aria-label="Upload a file">
          <div className="bulk-upload-fields">
            <div className="bulk-field">
              <label htmlFor="bulk-client">Client</label>
              <Form.Select
                id="bulk-client"
                value={selectedClientId}
                onChange={handleClientChange}
                disabled={isLoggedIn?.response?.role === "client"}
              >
                <option value="">Select a client</option>
                {visibleClients.map((client) => (
                  <option key={client._id} value={client._id}>
                    {client.username}
                  </option>
                ))}
              </Form.Select>
            </div>
            <div className="bulk-field">
              <label htmlFor="bulk-upload-input">File</label>
              <input
                id="bulk-upload-input"
                type="file"
                className="form-control bulk-file-input"
                accept=".csv,.xlsx"
                onChange={handleFileChange}
              />
            </div>
            <div className="bulk-upload-action">
              <button
                type="button"
                className="bulk-primary-button"
                onClick={handleUpload}
                disabled={uploading}
              >
                <FaFileUpload aria-hidden="true" />
                {uploading ? "Uploading..." : "Upload file"}
              </button>
            </div>
          </div>
          <p className="bulk-format-note">Template columns: title, description, long_link</p>
        </section>

        <section className="bulk-preview-panel" aria-labelledby="bulk-preview-title">
              <div className="bulk-panel-header">
                <div>
                  <h2 id="bulk-preview-title">Preview</h2>
                  <small className="bulk-muted">
                    50 rows per page
                  </small>
                </div>
                {currentUpload && (
                  <div className="bulk-preview-current">
                    <span className="bulk-current-upload">
                      {currentUpload.fileName} <span>{currentUpload.status}</span>
                    </span>
                    <small className="bulk-muted">
                      {Number(currentUpload.processedRows || 0).toLocaleString()} / {Number(currentUpload.totalRows || 0).toLocaleString()} processed
                      {` | ${Number(currentUpload.successRows || 0).toLocaleString()} success | ${Number(currentUpload.failedRows || 0).toLocaleString()} failed`}
                    </small>
                    {currentUpload.status !== "PROCESSING" && currentUpload.status !== "COMPLETED" && (
                      <button
                        type="button"
                        className="bulk-primary-button bulk-small-button"
                        onClick={() => handleStartProcessing(currentUpload._id)}
                        disabled={startingUploadId === currentUpload._id}
                      >
                        <FaPlay aria-hidden="true" />
                        {startingUploadId === currentUpload._id ? "Starting..." : "Begin Processing"}
                      </button>
                    )}
                    {currentUpload.status === "COMPLETED" && (
                      <a className="bulk-download-button" href={getBulkDownloadUrl(currentUpload._id)}>
                        <FaDownload aria-hidden="true" />
                        Download
                      </a>
                    )}
                  </div>
                )}
              </div>

              {!currentUpload && loadingPreview && (
                <div className="bulk-empty-state" role="status">Loading preview...</div>
              )}

              {!currentUpload && !previewError && !loadingPreview && (
                <div className="bulk-empty-state">Upload a file or open an existing upload to preview rows.</div>
              )}

              {previewError && <div className="bulk-empty-state" role="alert">{previewError}</div>}

              {currentUpload && (
                <>
                  <ResponsiveRows
                    rows={previewRows}
                    getRowId={(row) => row._id || row.rowNumber}
                    resetKey={`${currentUpload._id}-${previewPage}`}
                    renderSummary={(row) => <><strong>Row {row.rowNumber}: {row.title || row.long_link || "Untitled"}</strong><span>{row.status}</span></>}
                    renderDetails={(row) => (
                      <dl>
                        <div><dt>Title</dt><dd>{row.title || "-"}</dd></div>
                        <div><dt>Description</dt><dd>{row.description || "-"}</dd></div>
                        <div><dt>Long link</dt><dd>{row.long_link || "-"}</dd></div>
                        <div><dt>Status</dt><dd>{row.status}</dd></div>
                        <div><dt>Short code</dt><dd>{row.short_code || "-"}</dd></div>
                        {row.errorMessage && <div><dt>Error</dt><dd>{row.errorMessage}</dd></div>}
                      </dl>
                    )}
                  >
                  <div className="table-responsive">
                    <table className="table table-sm align-middle bulk-preview-table">
                      <thead>
                        <tr>
                          <th>Row</th>
                          <th>Title</th>
                          <th>Description</th>
                          <th>Long Link</th>
                          <th>Status</th>
                          <th>Short Code</th>
                        </tr>
                      </thead>
                      <tbody>
                        {previewRows.map((row) => (
                          <tr key={row._id}>
                            <td>{row.rowNumber}</td>
                            <td>{row.title}</td>
                            <td>{row.description}</td>
                            <td className="bulk-long-link">
                              {row.long_link}
                            </td>
                            <td>
                              <span className="bulk-status">{row.status}</span>
                            </td>
                            <td>{row.short_code || "-"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  </ResponsiveRows>

                  <div className="bulk-pagination">
                    <small className="bulk-muted">
                      Page {previewPage + 1} of {previewTotalPages}
                    </small>
                    <div className="bulk-pagination-actions">
                      <button
                        type="button"
                        className="bulk-secondary-button"
                        disabled={previewPage === 0}
                        onClick={() => loadPreviewRows(currentUpload._id, previewPage - 1)}
                      >
                        Previous
                      </button>
                      <button
                        type="button"
                        className="bulk-secondary-button"
                        disabled={previewPage + 1 >= previewTotalPages}
                        onClick={() => loadPreviewRows(currentUpload._id, previewPage + 1)}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </>
              )}
        </section>
      </section>
    </SideBar>
  );
};
